import { Plus, Trash2 } from "lucide-react";
import { centsToInputValue, dollarsInputToCents, BILLING_TYPE_LABELS } from "@/lib/money";

export default function QuoteItemsEditor({ items, onChange }) {
  const update = (idx, patch) => onChange(items.map((item, i) => (i === idx ? { ...item, ...patch } : item)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { item_id: `new_${Date.now()}`, description: "", quantity: 1, billing_type: "one_time", unit_price_cents: 0 }]);

  return (
    <div data-testid="quote-items-editor" className="space-y-3">
      {items.map((item, idx) => (
        <div
          key={item.item_id || idx}
          data-testid={`quote-item-row-${idx}`}
          className="grid grid-cols-1 gap-2 rounded-xl border border-slate-200 p-3 sm:grid-cols-[1fr_80px_180px_130px_36px] sm:items-center dark:border-white/10"
        >
          <input
            value={item.description}
            onChange={(e) => update(idx, { description: e.target.value })}
            placeholder="Description"
            data-testid={`quote-item-description-${idx}`}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
          />
          <input
            type="number"
            min={1}
            value={item.quantity}
            onChange={(e) => update(idx, { quantity: Math.max(1, Number(e.target.value) || 1) })}
            data-testid={`quote-item-quantity-${idx}`}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
          />
          <select
            value={item.billing_type}
            onChange={(e) => update(idx, { billing_type: e.target.value })}
            data-testid={`quote-item-billing-type-${idx}`}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
          >
            {Object.entries(BILLING_TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 dark:text-slate-500">$</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={centsToInputValue(item.unit_price_cents)}
              onChange={(e) => update(idx, { unit_price_cents: dollarsInputToCents(e.target.value) })}
              data-testid={`quote-item-price-${idx}`}
              className="w-full rounded-lg border border-slate-300 py-2 pl-6 pr-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
          </div>
          <button
            type="button"
            onClick={() => remove(idx)}
            data-testid={`quote-item-remove-${idx}`}
            className="rounded-lg p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600 dark:text-slate-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="quote-item-add-btn"
        className="inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 transition-colors duration-200 hover:border-blue-300 hover:text-blue-600 dark:border-white/20 dark:text-slate-300 dark:hover:border-blue-500/40 dark:hover:text-blue-400"
      >
        <Plus size={15} /> Add item
      </button>
    </div>
  );
}
